import type { Settings } from '@shared/types';

export interface HudCallbacks {
  onContinue(): void;
  onNewGame(): void;
  onOpenMenu(): void;
  onUndo(): void;
  onNextTray(): void;
  onArmRotate(): void;
  onPieceRotate(slot: number): void;
  onOpenOptions(): void;
  onOpenHowTo(): void;
  onOpenHighscores(): void;
  onOpenAccount(): void;
  onBackToMenu(): void;
  onSettingsChange(patch: Partial<Settings>): void;
}

const HOW_TO_PLAY_HTML = `
  <h2>How To Play</h2>
  <p style="text-align:center"><strong>Goal</strong><br/>Drag and drop blocks onto the board to create
  vertical and/or horizontal rows and increase your high score.</p>
  <p><strong>Conditions</strong></p>
  <p>• When placed, a block raises the value of its neighbours by its own value.</p>
  <p>• Once a full row or column is created it clears, and the combined value of all blocks in it is
  added to your score.</p>
  <p>• Clear multiple rows at once for an increasing score multiplier.</p>
  <p>• Spend a rotation point on a block to enable rotating it. Clear rows to earn more rotation points.</p>
  <p>• Undo the placement of your last block while still in the set it came from. You can also undo a
  rotation point given to a block.</p>
  <p>• If you can't place any more blocks from your current tray, the game is over.</p>
  <p style="text-align:center;color:var(--ink-dim)">The game saves automatically — come back any time and
  continue where you left off. Good luck and have fun!</p>
`;

export class Hud {
  private root: HTMLElement;
  private cb: HudCallbacks;
  // cached elements
  private hud!: HTMLElement;
  private menu!: HTMLElement;
  private gameover!: HTMLElement;
  private options!: HTMLElement;
  private howto!: HTMLElement;
  private highscores!: HTMLElement;
  private scoreEl!: HTMLElement;
  private bestEl!: HTMLElement;
  private rotateCountEl!: HTMLElement;
  private rotateBarEl!: HTMLElement;
  private rotateBtn!: HTMLElement;
  private undoBtn!: HTMLButtonElement;
  private nextTrayBtn!: HTMLButtonElement;
  private continueBtn!: HTMLButtonElement;
  private welcomeEl!: HTMLElement;
  private toastEl!: HTMLElement;
  private gameoverText!: HTMLElement;
  private pieceHandles: HTMLButtonElement[] = [];

  constructor(root: HTMLElement, cb: HudCallbacks) {
    this.root = root;
    this.cb = cb;
    this.build();
  }

  private build(): void {
    this.root.innerHTML = `
      <div id="hud" style="display:none">
        <div class="hud-top">
          <div class="hud-best" id="hud-best">Previous best : 0</div>
          <div class="hud-score" id="hud-score">Score : 0</div>
        </div>
        <button class="corner-btn btn-menu" id="btn-menu"><span class="ring">⌂</span><span class="label">Menu</span></button>
        <button class="corner-btn btn-options" id="btn-options2"><span class="ring">≡</span><span class="label">Options</span></button>
        <button class="corner-btn btn-rotate" id="btn-rotate">
          <span class="ring">⟳</span><span class="label">Rotate</span>
          <span class="rotate-count" id="rotate-count">1</span>
          <span class="rotate-bar"><i id="rotate-fill"></i></span>
        </button>
        <button class="corner-btn btn-undo" id="btn-undo"><span class="ring">↶</span><span class="label">Undo</span></button>
        <button class="piece-rotate" data-slot="0">⟳</button>
        <button class="piece-rotate" data-slot="1">⟳</button>
        <button class="piece-rotate" data-slot="2">⟳</button>
        <button class="next-tray-btn" id="btn-nexttray">Next Tray</button>
      </div>

      <div id="toast" class="toast"></div>

      <div id="menu" class="overlay hidden">
        <div class="welcome" id="welcome"></div>
        <div class="title-kanji shadowed">安息</div>
        <div class="title-roman shadowed">ANSOKU</div>
        <button class="menu-btn" id="m-continue">Continue Game</button>
        <button class="menu-btn" id="m-new">Start New Game</button>
        <button class="menu-btn" id="m-high">High Scores</button>
        <button class="menu-btn" id="m-rules">Game Rules</button>
        <button class="menu-btn" id="m-options">Options</button>
        <button class="menu-btn" id="m-account">Account</button>
      </div>

      <div id="gameover" class="overlay hidden">
        <div class="title-roman shadowed" style="letter-spacing:3px">Game Over</div>
        <div id="gameover-text" class="shadowed" style="font-size:22px;text-align:center;margin:10px 0"></div>
        <button class="menu-btn" id="go-new">Start New Game</button>
        <button class="menu-btn" id="go-menu">Main Menu</button>
      </div>

      <div id="options" class="overlay hidden">
        <div class="book">
          <div class="book-page book-left">
            <h2>Options</h2>
            <div class="row"><label>Music</label><input type="range" id="opt-music" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Sound</label><input type="range" id="opt-sound" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Dark mode</label><input type="checkbox" id="opt-dark"/></div>
            <div class="row"><label>Ambient sounds</label><input type="checkbox" id="opt-ambient"/></div>
            <div class="actions"><button class="menu-btn" id="opt-back">Back</button></div>
          </div>
          <div class="book-page book-right" id="amb-page">
            <h2>Ambient Mix</h2>
            <div class="row"><label>Fire</label><input type="range" id="amb-0" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Water</label><input type="range" id="amb-1" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Birds</label><input type="range" id="amb-2" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Chimes</label><input type="range" id="amb-3" min="0" max="1" step="0.01"/></div>
            <div class="row"><label>Chirps</label><input type="range" id="amb-4" min="0" max="1" step="0.01"/></div>
            <p class="amb-note">Turn on “Ambient sounds” to blend the soundscape.</p>
          </div>
        </div>
      </div>

      <div id="howto" class="overlay hidden">
        <div class="panel">${HOW_TO_PLAY_HTML}
          <div class="actions"><button class="menu-btn" id="howto-back">Back</button></div>
        </div>
      </div>

      <div id="highscores" class="overlay hidden">
        <div class="panel">
          <h2>High Scores</h2>
          <div id="hs-body"><p style="text-align:center;color:var(--ink-dim)">Your best: <span id="hs-local">0</span></p></div>
          <div class="actions"><button class="menu-btn" id="hs-back">Back</button></div>
        </div>
      </div>
    `;

    const $ = <T extends HTMLElement = HTMLElement>(id: string) => this.root.querySelector<T>('#' + id)!;
    this.hud = $('hud');
    this.menu = $('menu');
    this.gameover = $('gameover');
    this.options = $('options');
    this.howto = $('howto');
    this.highscores = $('highscores');
    this.scoreEl = $('hud-score');
    this.bestEl = $('hud-best');
    this.rotateCountEl = $('rotate-count');
    this.rotateBarEl = $('rotate-fill');
    this.rotateBtn = $('btn-rotate');
    this.undoBtn = $<HTMLButtonElement>('btn-undo');
    this.nextTrayBtn = $<HTMLButtonElement>('btn-nexttray');
    this.continueBtn = $<HTMLButtonElement>('m-continue');
    this.welcomeEl = $('welcome');
    this.toastEl = $('toast');
    this.gameoverText = $('gameover-text');
    this.pieceHandles = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.piece-rotate'));

    // wire events
    $('btn-menu').onclick = () => this.cb.onOpenMenu();
    $('btn-options2').onclick = () => this.cb.onOpenOptions();
    this.rotateBtn.onclick = () => this.cb.onArmRotate();
    this.undoBtn.onclick = () => this.cb.onUndo();
    this.nextTrayBtn.onclick = () => this.cb.onNextTray();
    this.pieceHandles.forEach((h) => {
      h.onclick = (e) => {
        e.stopPropagation();
        this.cb.onPieceRotate(Number(h.dataset.slot));
      };
    });

    $('m-continue').onclick = () => this.cb.onContinue();
    $('m-new').onclick = () => this.cb.onNewGame();
    $('m-high').onclick = () => this.cb.onOpenHighscores();
    $('m-rules').onclick = () => this.cb.onOpenHowTo();
    $('m-options').onclick = () => this.cb.onOpenOptions();
    $('m-account').onclick = () => this.cb.onOpenAccount();

    $('go-new').onclick = () => this.cb.onNewGame();
    $('go-menu').onclick = () => this.cb.onBackToMenu();

    $('opt-back').onclick = () => this.cb.onBackToMenu();
    $('howto-back').onclick = () => this.cb.onBackToMenu();
    $('hs-back').onclick = () => this.cb.onBackToMenu();

    const music = $<HTMLInputElement>('opt-music');
    const sound = $<HTMLInputElement>('opt-sound');
    const dark = $<HTMLInputElement>('opt-dark');
    const ambient = $<HTMLInputElement>('opt-ambient');
    music.oninput = () => this.cb.onSettingsChange({ musicLevel: Number(music.value) });
    sound.oninput = () => this.cb.onSettingsChange({ soundLevel: Number(sound.value) });
    dark.onchange = () => this.cb.onSettingsChange({ darkMode: dark.checked });
    ambient.onchange = () => {
      this.cb.onSettingsChange({ useAmbient: ambient.checked });
      this.setAmbientPageEnabled(ambient.checked);
    };

    // 5-channel ambient mixer (order: fire, water, birds, chimes, chirps).
    const ambSliders = [0, 1, 2, 3, 4].map((i) => $<HTMLInputElement>('amb-' + i));
    const readMix = () => ambSliders.map((s) => Number(s.value));
    ambSliders.forEach((s) => {
      s.oninput = () => this.cb.onSettingsChange({ ambientMix: readMix() });
    });
  }

  private setAmbientPageEnabled(on: boolean): void {
    const page = this.root.querySelector('#amb-page');
    if (page) page.classList.toggle('disabled', !on);
  }

  // ---- screen management ----
  private hideAllOverlays(): void {
    this.menu.classList.add('hidden');
    this.gameover.classList.add('hidden');
    this.options.classList.add('hidden');
    this.howto.classList.add('hidden');
    this.highscores.classList.add('hidden');
  }

  showGame(): void {
    this.hideAllOverlays();
    this.hud.style.display = 'block';
  }
  showMenu(): void {
    this.hideAllOverlays();
    this.hud.style.display = 'none';
    this.menu.classList.remove('hidden');
  }
  showGameOver(score: number, isHigh: boolean): void {
    this.gameoverText.innerHTML = isHigh
      ? `No more moves left.<br/><br/><span style="color:var(--gold)">New high score!</span><br/>Your score : ${score}`
      : `No more moves left.<br/><br/>Your score : ${score}`;
    this.gameover.classList.remove('hidden');
  }
  showOptions(): void {
    this.hideAllOverlays();
    this.options.classList.remove('hidden');
  }
  showHowTo(): void {
    this.hideAllOverlays();
    this.howto.classList.remove('hidden');
  }
  showHighscores(): void {
    this.hideAllOverlays();
    this.highscores.classList.remove('hidden');
  }

  // ---- updates ----
  setScore(n: number, flash = false): void {
    this.scoreEl.textContent = `Score : ${n}`;
    if (flash) {
      this.scoreEl.classList.remove('flash');
      void this.scoreEl.offsetWidth;
      this.scoreEl.classList.add('flash');
    }
  }
  setBest(text: string): void {
    this.bestEl.textContent = text;
  }
  setRotates(n: number): void {
    this.rotateCountEl.textContent = String(n);
  }
  setRotateBar(pct: number): void {
    this.rotateBarEl.style.width = `${Math.max(0, Math.min(1, pct)) * 100}%`;
  }
  setArming(on: boolean): void {
    this.rotateBtn.classList.toggle('arming', on);
  }
  setUndoEnabled(on: boolean): void {
    this.undoBtn.disabled = !on;
  }
  setNextTrayVisible(on: boolean): void {
    this.nextTrayBtn.classList.toggle('show', on);
  }
  setContinueEnabled(on: boolean): void {
    this.continueBtn.disabled = !on;
  }
  setWelcome(text: string): void {
    this.welcomeEl.textContent = text;
  }
  setAccountLabel(text: string): void {
    const el = this.root.querySelector('#m-account');
    if (el) el.textContent = text;
  }
  setLocalHighscore(n: number): void {
    const el = this.root.querySelector('#hs-local');
    if (el) el.textContent = String(n);
  }
  setHighscoreList(entries: { rank: number; username: string; highscore: number }[]): void {
    const body = this.root.querySelector('#hs-body');
    if (!body) return;
    if (!entries.length) return;
    const rows = entries
      .map((e) => `<div class="row"><span>${e.rank}. ${e.username}</span><span>${e.highscore}</span></div>`)
      .join('');
    body.innerHTML = `<div style="margin-top:6px">${rows}</div>`;
  }
  setPieceHandle(slot: number, screen: { x: number; y: number } | null): void {
    const h = this.pieceHandles[slot];
    if (!h) return;
    if (screen) {
      h.style.left = `${screen.x}px`;
      h.style.top = `${screen.y}px`;
      h.classList.add('show');
    } else {
      h.classList.remove('show');
    }
  }
  setOptionValues(s: Settings): void {
    (this.root.querySelector('#opt-music') as HTMLInputElement).value = String(s.musicLevel);
    (this.root.querySelector('#opt-sound') as HTMLInputElement).value = String(s.soundLevel);
    (this.root.querySelector('#opt-dark') as HTMLInputElement).checked = s.darkMode;
    (this.root.querySelector('#opt-ambient') as HTMLInputElement).checked = s.useAmbient;
    const mix = s.ambientMix ?? [];
    for (let i = 0; i < 5; i++) {
      const el = this.root.querySelector('#amb-' + i) as HTMLInputElement | null;
      if (el) el.value = String(mix[i] ?? 0);
    }
    this.setAmbientPageEnabled(s.useAmbient);
  }
  toast(text: string): void {
    this.toastEl.innerHTML = text;
    this.toastEl.classList.remove('show');
    void this.toastEl.offsetWidth;
    this.toastEl.classList.add('show');
  }
}
